import { zodResolver } from '@hookform/resolvers/zod';
import { DatePicker } from '@optools/components/datetime/date-picker';
import { Button } from '@optools/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { FeedPolicySearchType } from '@optools/services/operator-gateway/esni/types';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const FormSchema = z.object({
  searchValue: z
    .string()
    .min(1, { error: 'Search value must be non-empty' })
    .transform((value) => value.trim()),
});

export function SearchBy() {
  const { searchType, searchValue } = useSearch({ from: '/_auth/esni/media' });
  const navigate = useNavigate({ from: '/esni/media' });

  const form = useForm<z.infer<typeof FormSchema>>({
    defaultValues: { searchValue },
    resolver: zodResolver(FormSchema),
  });

  const handleOnSubmit = (data: z.infer<typeof FormSchema>) => {
    navigate({ replace: true, search: (params) => ({ ...params, searchValue: data.searchValue }) });
  };

  const handleOnValueChange = (value: FeedPolicySearchType) => {
    navigate({
      replace: true,
      search: (params) => ({ ...params, searchType: value, searchValue: '' }),
    });
    form.reset({ searchValue: '' });
  };

  return (
    <div className="flex gap-2">
      <Select value={searchType ?? FeedPolicySearchType.DATE} onValueChange={handleOnValueChange}>
        <SelectTrigger aria-label="Search By" className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FeedPolicySearchType.DATE}>Date</SelectItem>
          <SelectItem value={FeedPolicySearchType.GAME}>Game PK</SelectItem>
          <SelectItem value={FeedPolicySearchType.MEDIA}>Media ID</SelectItem>
        </SelectContent>
      </Select>
      {searchType === FeedPolicySearchType.DATE ? (
        <DatePicker
          date={searchValue}
          setDate={(date) => {
            navigate({ replace: true, search: (params) => ({ ...params, searchValue: date }) });
          }}
        />
      ) : (
        <Form {...form}>
          <form className="flex gap-2" onSubmit={form.handleSubmit(handleOnSubmit)}>
            <FormField
              control={form.control}
              name="searchValue"
              render={({ field }) => (
                <FormItem>
                  <Input
                    {...field}
                    className="w-80"
                    placeholder={searchType === FeedPolicySearchType.GAME ? 'Game PK' : 'Media ID'}
                  />
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <Button type="submit">Search</Button>
          </form>
        </Form>
      )}
    </div>
  );
}
